import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  UI_MODE_ATTRIBUTE,
  UI_MODE_DARK,
  UI_MODE_KEY,
  UI_MODE_LIGHT,
  UI_THEME_ATTRIBUTE,
  UI_THEME_CLASSIC,
  UI_THEME_KEY,
  UI_THEME_MODERN,
  UiTheme,
  UiThemeMode
} from '../models/ui-theme';

/**
 * Arayüz temasını (klasik / modern) yöneten servis.
 *
 * Tek sorumluluğu: geçerli temayı bilmek, saklamak ve body attribute'unu güncellemek.
 * Faz 6'da tercih arka uca taşınırsa değişecek tek yer burasıdır.
 */
@Injectable({ providedIn: 'root' })
export class UiThemeService {

  private readonly themeSubject: BehaviorSubject<UiTheme>;
  private readonly modeSubject: BehaviorSubject<UiThemeMode>;

  /** Tema değişimlerini dinlemek isteyen bileşenler için. Mevcut değeri de yayınlar. */
  readonly theme$: Observable<UiTheme>;

  /** Modern temanın açık/koyu modu. */
  readonly mode$: Observable<UiThemeMode>;

  constructor() {
    this.themeSubject = new BehaviorSubject<UiTheme>(this.read());
    this.theme$ = this.themeSubject.asObservable();

    this.modeSubject = new BehaviorSubject<UiThemeMode>(this.readMode());
    this.mode$ = this.modeSubject.asObservable();

    // index.html'deki FOUC script'i temayı zaten yazmış olabilir; burada durumu garantiye alıyoruz.
    this.apply(this.themeSubject.value);
    this.applyMode(this.modeSubject.value);
  }

  get current(): UiTheme {
    return this.themeSubject.value;
  }

  get isModern(): boolean {
    return this.current === UI_THEME_MODERN;
  }

  /**
   * Tema geçişinin kullanıcıya gösterilip gösterilmeyeceği.
   *
   * Modern tema varsayılan olduğu için kontrol normalde her zaman görünür —
   * kullanıcının eski tasarıma dönüş yolu kapanmamalı. Yalnızca acil durum
   * anahtarı (`newDesignEnabled: false`) kapalıyken gizlenir; o durumda zaten
   * herkes klasik temadadır.
   */
  get isSwitchVisible(): boolean {
    return environment.newDesignEnabled !== false;
  }

  setTheme(theme: UiTheme): void {
    const safe: UiTheme = theme === UI_THEME_MODERN ? UI_THEME_MODERN : UI_THEME_CLASSIC;

    try {
      localStorage.setItem(UI_THEME_KEY, safe);
    } catch (e) {
      // Gizli sekme / kapalı depolama: tema yine de bu oturum için uygulanır.
    }

    this.apply(safe);

    if (safe !== this.themeSubject.value) {
      this.themeSubject.next(safe);
    }
  }

  toggle(): void {
    this.setTheme(this.isModern ? UI_THEME_CLASSIC : UI_THEME_MODERN);
  }

  // ------------------------------------------------------- açık / koyu mod

  get currentMode(): UiThemeMode {
    return this.modeSubject.value;
  }

  get isDark(): boolean {
    return this.currentMode === UI_MODE_DARK;
  }

  setMode(mode: UiThemeMode): void {
    const safe: UiThemeMode = mode === UI_MODE_DARK ? UI_MODE_DARK : UI_MODE_LIGHT;

    try {
      localStorage.setItem(UI_MODE_KEY, safe);
    } catch (e) {
      // gizli sekme / kapalı depolama: mod yalnızca bu oturum için geçerli olur
    }

    this.applyMode(safe);

    if (safe !== this.modeSubject.value) {
      this.modeSubject.next(safe);
    }
  }

  toggleMode(): void {
    this.setMode(this.isDark ? UI_MODE_LIGHT : UI_MODE_DARK);
  }

  /**
   * Varsayılan tema **modern**: kullanıcı açıkça "eski tasarıma dön" demediyse
   * yeni tasarım açılır.
   *
   * `newDesignEnabled: false` bir acil durum anahtarıdır: bayrak kapalıyken
   * herkes klasik temaya döner ve geçiş kontrolü gizlenir.
   *
   * DİKKAT: index.html'deki FOUC script'i de aynı varsayılanı uygular;
   * biri değişirse diğeri de değişmeli.
   */
  private read(): UiTheme {
    if (environment.newDesignEnabled === false) {
      return UI_THEME_CLASSIC;
    }

    try {
      return localStorage.getItem(UI_THEME_KEY) === UI_THEME_CLASSIC ? UI_THEME_CLASSIC : UI_THEME_MODERN;
    } catch (e) {
      return UI_THEME_MODERN;
    }
  }

  /**
   * Koyu mod varsayılan değildir; kullanıcı açıkça seçer.
   * (İşletim sistemi tercihini izlemek istenirse `prefers-color-scheme`
   * buraya ve index.html script'ine birlikte eklenmeli.)
   */
  private readMode(): UiThemeMode {
    try {
      return localStorage.getItem(UI_MODE_KEY) === UI_MODE_DARK ? UI_MODE_DARK : UI_MODE_LIGHT;
    } catch (e) {
      return UI_MODE_LIGHT;
    }
  }

  /** Açık modda attribute tamamen kaldırılır. */
  private applyMode(mode: UiThemeMode): void {
    if (mode === UI_MODE_DARK) {
      document.body.setAttribute(UI_MODE_ATTRIBUTE, UI_MODE_DARK);
    } else {
      document.body.removeAttribute(UI_MODE_ATTRIBUTE);
    }
  }

  /** Klasik temada attribute tamamen kaldırılır; boş değer bırakılmaz. */
  private apply(theme: UiTheme): void {
    if (theme === UI_THEME_MODERN) {
      document.body.setAttribute(UI_THEME_ATTRIBUTE, UI_THEME_MODERN);
    } else {
      document.body.removeAttribute(UI_THEME_ATTRIBUTE);
    }
  }
}
